/**
 * Política de contraseñas centralizada.
 *
 * Diseñada para cumplir:
 *   - HIPAA: controles razonables contra credenciales débiles.
 *   - ISO 27001 (Anexo A.9.4.3): "sistemas de gestión de contraseñas
 *     deben asegurar contraseñas de calidad".
 *   - PCI DSS v4.0 (8.3.6): mínimo 12 chars, mayús+minús+número+símbolo.
 *
 * Si en el futuro hay que ajustar las reglas (cambia la auditoría,
 * cambia la política interna), edita este archivo y nada más.
 * El validator y el componente de fuerza leen estas constantes.
 */

export const PASSWORD_POLICY = {
    /** Longitud mínima en caracteres */
    minLength: 12,

    /** Longitud máxima (NIST recomienda permitir hasta 64+) */
    maxLength: 128,

    /** ¿Debe incluir al menos una mayúscula? */
    requireUppercase: true,

    /** ¿Debe incluir al menos una minúscula? */
    requireLowercase: true,

    /** ¿Debe incluir al menos un número? */
    requireNumber: true,

    /** ¿Debe incluir al menos un símbolo? */
    requireSymbol: true,

    /** Caracteres aceptados como símbolos (usado en validación y mensaje al usuario) */
    symbolChars: '!@#$%^&*()_+-=[]{}|;:,.<>?/~`"\'\\',

    /** ¿Bloquear si tiene 3+ caracteres iguales seguidos? (aaa, 111) */
    blockRepeatedChars: true,

    /** ¿Bloquear si tiene secuencias evidentes? (abc, 123, qwerty) */
    blockSequences: true,

    /** ¿Bloquear si la contraseña contiene el email del usuario? */
    blockEmailMatch: true,

    /** ¿Bloquear si la contraseña contiene el nombre del usuario? */
    blockNameMatch: true,

    /** ¿Bloquear si la contraseña está en la lista de top-comunes? */
    blockCommonPasswords: true,
} as const;

/**
 * Lista de contraseñas comunes prohibidas.
 *
 * Top contraseñas más usadas mundialmente (filtraciones reales) +
 * variantes en español. Incluye combinaciones que pasarían las 4
 * categorías de complejidad (mayús+minús+número+símbolo) pero que
 * siguen siendo predecibles.
 *
 * La comparación se hace en lowercase, así que NO incluyas variantes
 * con sólo el case cambiado.
 */
export const COMMON_PASSWORDS: readonly string[] = [
    // Top universales
    'password', 'passw0rd', 'p@ssw0rd', 'p@ssword', 'pa$$w0rd',
    'password1', 'password12', 'password123', 'password1234',
    'password!', 'password@', 'password#', 'password$',
    '123456', '12345678', '123456789', '1234567890', '12345',
    'qwerty', 'qwerty123', 'qwertyuiop', 'qwerty12',
    'abc123', 'abcd1234', 'abcdef', 'abcdefg',
    'admin', 'admin123', 'admin@123', 'administrator',
    'welcome', 'welcome123', 'welcome1', 'welcome@123',
    'letmein', 'letmein123', 'letmein!',
    'iloveyou', 'iloveyou1', 'iloveyou123',
    'monkey', 'monkey123', 'dragon', 'dragon123',
    'master', 'master123', 'shadow', 'shadow123',
    'football', 'baseball', 'superman', 'batman',
    'sunshine', 'princess', 'pokemon', 'starwars',

    // Variantes con símbolos que pasan las 4 categorías
    'Password1!', 'Password123!', 'Password@123', 'Password#1',
    'Welcome1!', 'Welcome123!', 'Welcome@123',
    'Admin@123', 'Admin123!', 'Administrator1',
    'Qwerty1!', 'Qwerty123!', 'Qwerty@123',
    'Letmein1!', 'Letmein123!',

    // Español comunes
    'contraseña', 'contrasena', 'contrasena123', 'contraseña1',
    'usuario', 'usuario123', 'usuario1',
    'bienvenido', 'bienvenido1', 'bienvenido123',
    'hola', 'hola123', 'holamundo',
    'mexico', 'mexico123', 'mexico1',
    'amor', 'amor123', 'teamo', 'teamo123',
    'futbol', 'futbol123', 'beisbol',

    // Patrones de teclado
    'asdfgh', 'asdfghjkl', 'asdf1234',
    'zxcvbn', 'zxcvbnm', 'zxcvbn123',
    '1q2w3e4r', '1q2w3e', '1qaz2wsx', '1qazxsw2',
    '!@#$%^&*', '!@#$%',

    // Año + variaciones
    '2020', '2021', '2022', '2023', '2024', '2025', '2026',
    'Password2024', 'Password2025', 'Password2026',
    'Welcome2024', 'Welcome2025', 'Welcome2026',

    // Empresariales típicos
    'company', 'company123', 'changeme', 'changeme123',
    'default', 'default123', 'temporal', 'temporal123',
    'temp', 'temp123', 'temppass', 'firsttime',

    // Específicos del contexto (médico/biomédico)
    'biomedica', 'biomedica123', 'hitec', 'hitec123',
    'epione', 'epione123', 'medical', 'medical123',
    'hospital', 'hospital123',
];